import { computed, ComputedRef, reactive, Ref, ref } from 'vue';
import {
    AddBasketItemRequest,
    B2BClientService,
    BasketDeliverable,
    BasketModel,
    BasketModelSummary,
    ReplaceItemRequest,
    AddOrUpdateModuleConfigurationRequest,
    ChangeDeliveryTypeRequest,
    BaseItem,
    CheckoutDeliverableType,
    BaseProductItem, AddLineRequisitionRequest, AddRequisitionAndCommentRequest, RequisitionAndCommentModel,
} from '@/b2b/shared/api/server-apis';
import Deferred from '@/core/async/Deferred';
import utilities from '@/shared/utilities.service';

export interface IBasketState {
    pending: boolean;
    basket: BasketModel;
}

const state: IBasketState = reactive({
    pending: false,
    basket: {} as BasketModel,
});

const basketId: Ref<string> = ref('');
const initDeferred = new Deferred<BasketModel>();
const initialized: Promise<BasketModel> = initDeferred.promise;

export default function useBasket() {
    const pending: ComputedRef<boolean> = computed(() => state.pending);
    const basket: ComputedRef<BasketModel> = computed(() => state.basket);
    const deliverables: ComputedRef<BasketDeliverable[]> = computed(() => state.basket?.deliverables ?? [] as BasketDeliverable[]);
    const summary: ComputedRef<BasketModelSummary> = computed(() => state.basket?.summary ?? {} as BasketModelSummary);
    const quantity: ComputedRef<number> = computed(() => state.basket?.numberOfItems ?? 0);
    const upSellItemCounter: ComputedRef<number> = computed(() => state.basket?.upSellItemCounter ?? 0);
    const isEmpty: ComputedRef<boolean> = computed(() => state.basket?.numberOfItems < 1 ?? false);
    const isLoading: ComputedRef<boolean> = computed(() => quantity.value < 1 && !isEmpty.value);
    const isCheckoutable: ComputedRef<boolean> = computed(() => state.basket.isCheckoutAble);
    const pickAndPackDeliveryMaximumQuantity: ComputedRef<number> = computed(() => state.basket.pickAndPackDeliveryMaximumQuantity ?? 0);
    const lineItems: ComputedRef<BaseItem[]> = computed(() => {
        const items = [] as BaseItem[];
        deliverables.value.forEach(deliverable => items.push(...deliverable.items));
        return items;
    });

    const lineItemTotalCount:ComputedRef<number> = computed(() => {
        let count = 0;
        deliverables.value.forEach(deliverable => count += deliverable.items.length);
        return count;
    });

    const requisitionAndComment = computed<RequisitionAndCommentModel>(() => {
        return basket.value.requisitionAndComment ?? {} as RequisitionAndCommentModel;
    });

    const init = async(id: string): Promise<BasketModel> => {
        basketId.value = id;
        try {
            const basket = await get();
            initDeferred.resolve(basket);
            return basket;
        } catch (e) {
            return Promise.reject();
        }
    };

    const get = async(): Promise<BasketModel> => {
        return B2BClientService.getBasket(basketId.value)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const replaceItem = async(request: ReplaceItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.replaceItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addRegular = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.addRegularItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addPickAndPack = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.addPickAndPackItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const deleteBasket = async(messageId?: string): Promise<BasketModel> => {
        return B2BClientService.deleteBasket(basketId.value, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const updateItemQuantity = async(itemId: number, request: { quantity: number, delta: boolean }, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }

        state.pending = true;
        await B2BClientService.updateItemQuantity(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);

        return state.basket;
    };

    const deleteItem = async(itemId: number, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2BClientService.deleteItem(basketId.value, itemId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const deleteItemBySku = async(sku: string, messageId?: string): Promise<BasketModel> => {
        const itemId = lineItems.value.find(item => (item as BaseProductItem).sku === sku)?.id ?? null;
        if (itemId) {
            return deleteItem(itemId, messageId);
        } else {
            return state.basket;
        }
    };

    const changeToRegular = async(request: ChangeDeliveryTypeRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2BClientService.changeToRegular(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeToPickAndPack = async(request: ChangeDeliveryTypeRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2BClientService.changeToPickAndPack(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const setPickAndPackBySku = async(sku: string, pickAndPack: boolean, messageId ?: string): Promise<BasketModel> => {
        const itemId = lineItems.value.find(item => (item as BaseProductItem).sku === sku)?.id ?? null;
        if (itemId) {
            const request = {
                itemIds: [itemId],
            };
            const apiMethod = pickAndPack ? changeToPickAndPack : changeToRegular;
            return apiMethod(request, messageId);
        } else {
            return state.basket;
        }
    };

    const addOrUpdateModuleConfiguration = async(configurationId: string, request: AddOrUpdateModuleConfigurationRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.addOrUpdateModuleConfiguration(basketId.value, configurationId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const getIsPickAndPack = (sku: string): boolean => {
        const pickAndPackDeliverable = deliverables.value.find(deliverable => deliverable.type === CheckoutDeliverableType.PickAndPack);
        if (!pickAndPackDeliverable) {
            return false;
        }
        return pickAndPackDeliverable.items.find(item => (item as BaseProductItem).sku === sku) !== undefined;
    };

    const addLineItemRequisition = async(itemId: number, request: AddLineRequisitionRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.requisition(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addRequisitionAndComment = async(request: AddRequisitionAndCommentRequest, messageId?: string): Promise<BasketModel> => {
        return B2BClientService.addRequisitionAndComment(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    return {
        initialized,
        basket,
        basketId,
        quantity,
        deliverables,
        summary,
        isEmpty,
        isLoading,
        isCheckoutable,
        pickAndPackDeliveryMaximumQuantity,
        lineItems,
        pending,
        lineItemTotalCount,
        upSellItemCounter,
        requisitionAndComment,
        deleteBasket,
        updateItemQuantity,
        deleteItem,
        deleteItemBySku,
        addRegular,
        addPickAndPack,
        get,
        replaceItem,
        init,
        changeToPickAndPack,
        changeToRegular,
        setPickAndPackBySku,
        addOrUpdateModuleConfiguration,
        getIsPickAndPack,
        addLineItemRequisition,
        addRequisitionAndComment,
    };
}

function setBasket(basket: BasketModel | undefined): BasketModel {
    state.basket = basket ?? {} as BasketModel;
    return state.basket;
}
